/**
 * Run-length index sets used by undo records.
 *
 * Stored as a flat Uint32Array of [start, end) pairs. Selections are usually
 * spatially coherent and edits like "select all" collapse to one run, so undo
 * history costs a few bytes per run instead of a byte per splat.
 */
export class Ranges {
    readonly data: Uint32Array;
    readonly count: number;

    private constructor(data: Uint32Array, count: number) {
        this.data = data;
        this.count = count;
    }

    static fromPredicate(n: number, pred: (i: number) => boolean): Ranges {
        const runs: number[] = [];
        let count = 0;
        let start = -1;
        for (let i = 0; i < n; i++) {
            if (pred(i)) {
                if (start < 0) start = i;
                count++;
            } else if (start >= 0) {
                runs.push(start, i);
                start = -1;
            }
        }
        if (start >= 0) runs.push(start, n);
        return new Ranges(Uint32Array.from(runs), count);
    }

    static fromSortedIndices(indices: ArrayLike<number>): Ranges {
        const runs: number[] = [];
        let i = 0;
        while (i < indices.length) {
            const start = indices[i];
            let end = start + 1;
            i++;
            while (i < indices.length && indices[i] === end) { end++; i++; }
            runs.push(start, end);
        }
        return new Ranges(Uint32Array.from(runs), indices.length);
    }

    static all(n: number): Ranges {
        return new Ranges(n > 0 ? Uint32Array.from([0, n]) : new Uint32Array(0), n);
    }

    get empty(): boolean {
        return this.count === 0;
    }

    forEach(fn: (i: number) => void): void {
        const d = this.data;
        for (let r = 0; r < d.length; r += 2) {
            for (let i = d[r]; i < d[r + 1]; i++) fn(i);
        }
    }

    /** Materialise as a sorted index list. */
    toIndices(): Uint32Array {
        const out = new Uint32Array(this.count);
        let o = 0;
        const d = this.data;
        for (let r = 0; r < d.length; r += 2) {
            for (let i = d[r]; i < d[r + 1]; i++) out[o++] = i;
        }
        return out;
    }

    get byteLength(): number {
        return this.data.byteLength;
    }
}
